import { useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import {
  GalleryRow,
  GalleryLightbox,
  GallerySharedStyles,
  type GalleryImage,
} from './_galleryShared';

/**
 * Single photo gallery for the Gallery page — renders every image in the
 * property's `photo_gallery` collection in one grid (with a shared
 * lightbox). Unlike the older split Apartment/Community sections, there is
 * no title filter, so the whole gallery shows in a single section.
 */
export function Gallery() {
  const images = useCollectionBinding<GalleryImage>('gallery.images', {
    collection: 'photo_gallery',
  });

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (images.length === 0) {
    return (
      <section className="bg-white">
        <div className="gallery-constrained py-12 text-center text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Gallery images will be displayed here.</p>
        </div>
        <GallerySharedStyles />
      </section>
    );
  }

  return (
    <section className="bg-white">
      <div className="gallery-constrained pt-8 md:pt-12 pb-12">
        <GalleryRow images={images} onSelect={setSelectedIndex} />
      </div>

      <GalleryLightbox
        images={images}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Gallery',
  displayName: 'Gallery',
  tokens: {},
  collectionBindings: {
    'gallery.images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      description: 'Pulled from the property photo gallery. Every photo appears in this grid.',
    },
  },
};

(Gallery as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Gallery;
